import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSelect } from '@angular/material/select';
import {
  MatStep,
  MatStepper,
  MatStepperNext,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { Router } from '@angular/router';
import { exhaustMap, Observable, Subscription, switchMap, tap, timer } from 'rxjs';

import {
  CoreExternalDocument,
  CoreJsonObject,
  CoreProviderImportResult,
} from '@core/api/core';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { CommonService } from '@shared/services/common.service';
import { FoldersService } from '@shared/services/folders.service';
import { VeevaService } from '@shared/services/integrations/veeva.service';

import { allowedStatuses } from '../../../../enums/status';
import { LogService } from '../../../../services/logging/log.service';
import { DefaultLogSchema } from '../../../../services/logging/schemas/defult.log-schema';
import { VeevaDynamicFiltersComponent } from './veeva-dynamic-filters/veeva-dynamic-filters.component';

interface VeevaPreviewPagination {
  page: number;
  limit: number;
  total: number;
}

type VeevaImportState =
  | 'idle'
  | 'creating-folder'
  | 'submitting'
  | 'running'
  | 'stopping'
  | 'no-matches'
  | 'cancelled'
  | 'status-unavailable'
  | 'failed';

@Component({
  selector: 'app-new-veeva-modal',
  templateUrl: './new-veeva-modal.component.html',
  styleUrl: './new-veeva-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatStepper,
    MatStep,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatError,
    MatInput,
    VeevaDynamicFiltersComponent,
    MatProgressBar,
    MatButton,
    MatIcon,
    MatCheckbox,
    MatPaginator,
    MatStepperNext,
    MatStepperPrevious,
    TitleCasePipe,
    SpinnerComponent,
  ],
})
export class NewVeevaModalComponent implements OnDestroy {
  private readonly _veevaService = inject(VeevaService);
  private readonly _commonService = inject(CommonService);
  private readonly _folderService = inject(FoldersService);
  private readonly _dialogRef = inject(MatDialogRef<NewVeevaModalComponent>);
  private readonly _router = inject(Router);
  private readonly _logger = inject(LogService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _subs = new Subscription();
  private readonly _logs = this._logger.createBuilder(DefaultLogSchema);
  private _importStatusSub: Subscription | null = null;
  private _selectedFolderId: string | null = null;

  protected readonly previewLoading = signal(false);
  protected readonly filtersLoading = signal(true);
  protected readonly results = signal<CoreExternalDocument[]>([]);
  protected readonly selectedResults = signal<Record<string, CoreExternalDocument>>({});
  protected readonly importAllByQuery = signal(false);
  protected readonly filters = signal<CoreJsonObject>({});
  protected readonly pagination = signal<VeevaPreviewPagination | null>(null);
  protected readonly importState = signal<VeevaImportState>('idle');
  protected readonly statuses = allowedStatuses;
  protected readonly importLimit = 1000;
  protected readonly importBusy = computed(() =>
    ['creating-folder', 'submitting', 'stopping'].includes(this.importState()),
  );
  protected readonly hasSubmittedImport = computed(() =>
    ['running', 'stopping', 'no-matches', 'cancelled', 'status-unavailable'].includes(
      this.importState(),
    ),
  );

  protected readonly folderForm = this._formBuilder.nonNullable.group({
    name: ['New Veeva folder', [Validators.required]],
    status: [allowedStatuses[0], [Validators.required]],
  });

  constructor() {
    this._logs.setValue('scope', 'veeva');
    effect(() => {
      this._dialogRef.disableClose = this.importBusy();
    });
  }

  ngOnDestroy(): void {
    this._importStatusSub?.unsubscribe();
    this._subs.unsubscribe();
  }

  protected onFiltersChanged(filters: CoreJsonObject): void {
    this.filters.set(filters);
    if (this.pagination()) {
      this.results.set([]);
      this.selectedResults.set({});
      this.importAllByQuery.set(false);
      this.pagination.set(null);
    }
  }

  protected onSearch(): void {
    this.results.set([]);
    this.selectedResults.set({});
    this.importAllByQuery.set(false);
    this.pagination.set(null);
    this._fetchPage(1, 20);
  }

  protected onPageChange(page: number, pageSize: number): void {
    this._fetchPage(page, pageSize);
  }

  protected isSelected(document: CoreExternalDocument): boolean {
    return document.externalId in this.selectedResults();
  }

  protected onCheckboxChange(event: MatCheckboxChange, document: CoreExternalDocument): void {
    this.selectedResults.update(selected => {
      const next = { ...selected };
      if (event.checked) {
        next[document.externalId] = document;
      } else {
        delete next[document.externalId];
      }
      return next;
    });
  }

  protected removeSelectedItem(externalId: string): void {
    this.selectedResults.update(selected => {
      const next = { ...selected };
      delete next[externalId];
      return next;
    });
  }

  protected selectedDocuments(): CoreExternalDocument[] {
    return Object.values(this.selectedResults());
  }

  protected selectedResultsAreValid(): boolean {
    return Object.keys(this.selectedResults()).length > 0;
  }

  protected onImportAllByQueryChange(event: MatCheckboxChange): void {
    this.importAllByQuery.set(event.checked);
    if (event.checked) {
      this.selectedResults.set({});
    }
  }

  protected canProceedToNextStep(): boolean {
    return this.importAllByQuery() || this.selectedResultsAreValid();
  }

  protected canProceedImport(): boolean {
    const folderIsReady = this._selectedFolderId !== null || this.folderForm.valid;
    return folderIsReady && this.canProceedToNextStep();
  }

  protected onSubmit(): void {
    if (!this.canProceedImport() || this.importBusy()) {
      return;
    }

    this.importState.set('creating-folder');
    const importRequest$ = this._selectedFolderId
      ? this._runImport(this._selectedFolderId)
      : this._folderService
          .createFolder(this.folderForm.getRawValue())
          .pipe(
            tap(folder => {
              this._selectedFolderId = folder._id;
              this.folderForm.disable();
              this.importState.set('submitting');
            }),
            switchMap(folder => this._runImport(folder._id)),
          );

    if (this._selectedFolderId) {
      this.importState.set('submitting');
    }

    const importSub = importRequest$.subscribe({
      next: result => {
        this.importState.set(result.count > 0 ? 'running' : 'no-matches');
        this._commonService.openSuccessSnackBar(result.message);
        if (result.count > 0) {
          this._startStatusPolling();
        }
      },
      error: error => {
        this.importState.set('failed');
        this._commonService.openErrorSnackBar('Import failed');
        this._logger.logError(error, { scope: 'veeva', action: 'import' });
      },
    });
    this._subs.add(importSub);
  }

  protected stopImport(): void {
    if (this.importState() !== 'running') {
      return;
    }

    this.importState.set('stopping');
    const stopSub = this._veevaService.stopImport().subscribe({
      next: message => {
        this.importState.set('cancelled');
        this._stopStatusPolling();
        this._commonService.openSuccessSnackBar(message);
      },
      error: error => {
        this.importState.set('running');
        this._commonService.openErrorSnackBar('Failed to stop import');
        this._logger.logError(error, { scope: 'veeva', action: 'stopImport' });
      },
    });
    this._subs.add(stopSub);
  }

  protected onClose(): void {
    if (this.importBusy()) {
      return;
    }

    this._dialogRef.close();
    this._folderService.fetchFolders();
    void this._router.navigate(['/sources'], {
      queryParams: this._selectedFolderId ? { folder_id: this._selectedFolderId } : undefined,
    });
  }

  private _fetchPage(page: number, limit: number): void {
    this.previewLoading.set(true);
    this.results.set([]);

    const previewSub = this._veevaService.previewDocuments(this.filters(), page, limit).subscribe({
      next: result => {
        this.results.set(result.documents);
        this.pagination.set({ page, limit, total: result.totalCount });
        this._logs.setValue('logData', {
          filters: this.filters(),
          resultsQty: result.documents.length,
        });
      },
      error: error => {
        this.pagination.set(null);
        this._commonService.openErrorSnackBar('Failed to fetch Veeva documents');
        this._logs.setError(error).log();
        this.previewLoading.set(false);
      },
      complete: () => {
        this.previewLoading.set(false);
        this._logs.log();
      },
    });
    this._subs.add(previewSub);
  }

  private _runImport(folderId: string): Observable<CoreProviderImportResult> {
    if (this.importAllByQuery()) {
      return this._veevaService.importByFilters(this.filters(), folderId, this.importLimit);
    }

    return this._veevaService.importByIds(Object.keys(this.selectedResults()), folderId);
  }

  private _startStatusPolling(): void {
    this._stopStatusPolling();
    this._importStatusSub = timer(3000, 3000)
      .pipe(exhaustMap(() => this._veevaService.getImportStatus()))
      .subscribe({
        next: status => {
          switch (status) {
            case 'running':
              this.importState.set('running');
              return;
            case 'cancelled':
              this.importState.set('cancelled');
              this._stopStatusPolling();
              return;
            case 'unknown':
              this.importState.set('status-unavailable');
              this._stopStatusPolling();
              return;
            default: {
              const exhaustive: never = status;
              throw new Error(`Unsupported Veeva import status: ${exhaustive}`);
            }
          }
        },
        error: error => {
          this.importState.set('status-unavailable');
          this._logger.logError(error, { scope: 'veeva', action: 'getImportStatus' });
          this._stopStatusPolling();
        },
      });
  }

  private _stopStatusPolling(): void {
    this._importStatusSub?.unsubscribe();
    this._importStatusSub = null;
  }
}
